import type { ChatMessage } from '../types/chat';

/**
 * CORE-1253 — как чат эфира складывает историю хаба и живые сообщения.
 *
 * Хаб отдаёт историю одним кадром `ChatHistory` на каждый `JoinLiveChat`:
 * при первом входе, после реконнекта и после смены токена. Раньше этот кадр
 * целиком ЗАМЕНЯЛ список, а `NewMessage` дописывался в хвост без проверки.
 * Отсюда три поломки позднего входа и реконнекта:
 *   - сообщение, пришедшее, пока сервер читал историю, приезжало дважды —
 *     живым кадром и строкой истории;
 *   - сообщение, пришедшее ДО кадра истории, этим кадром стиралось;
 *   - порядок был порядком прихода, а не временем отправки.
 *
 * Поэтому история не заменяет список, а сливается с ним: строка истории
 * побеждает свою копию по id, порядок — по времени отправки. Внутри окна,
 * которое история покрывает, она главная: строки из этого окна, которых в ней
 * нет, сервер больше не отдаёт, и держать их на экране незачем — так прежняя
 * «замена» продолжает работать там, где она была права. Вне окна (старше
 * предела истории или новее её последней строки) остаётся то, что уже было.
 */

const timeOf = (m: ChatMessage): number => {
  const t = Date.parse(m.createdAt);
  // Без времени строка считается самой свежей: так она не выпадет из окна
  // истории и не уедет в начало ленты.
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

/** Время отправки, при равенстве — id: сервер выдаёт его по возрастанию. */
const byTimeThenId = (a: ChatMessage, b: ChatMessage): number =>
  timeOf(a) - timeOf(b) || a.id - b.id;

const keepTail = (list: ChatMessage[], max: number): ChatMessage[] =>
  list.length > max ? list.slice(-max) : list;

export function mergeLiveChatHistory(
  prev: ChatMessage[],
  history: ChatMessage[],
  max: number,
): ChatMessage[] {
  const byId = new Map<number, ChatMessage>();
  for (const m of history) byId.set(m.id, m);
  const fresh = [...byId.values()].sort(byTimeThenId);
  // Пустой кадр ничего не говорит о том, что уже на экране: история чата не
  // должна пропадать из-за одного неудачного ответа.
  if (fresh.length === 0) return prev;

  const first = fresh[0];
  const last = fresh[fresh.length - 1];
  const outsideWindow = prev.filter(m =>
    !byId.has(m.id) && (byTimeThenId(m, first) < 0 || byTimeThenId(m, last) > 0));

  return keepTail([...fresh, ...outsideWindow].sort(byTimeThenId), max);
}

export function appendLiveChatMessage(
  prev: ChatMessage[],
  message: ChatMessage,
  max: number,
): ChatMessage[] {
  if (prev.some(m => m.id === message.id)) return prev;
  return keepTail([...prev, message].sort(byTimeThenId), max);
}
